import { isOnServer } from '@shared/constants/util'
import { medallia, MEDALLIA_DELAYED_ROUTES } from '@/lib/medallia'
import {
  getPageData,
  shouldDisableAnalytics,
  urlPathnameToLocationName,
} from '@/features/shared/analytics/utils'
import { getTrackOptions } from '@/features/shared/analytics/trackingQueue'
import ShiptAnalytics, { getAnonymousId } from '@shipt/analytics-member-web'
import { type PageEventProperties } from '@shipt/analytics-core'
import { REDIRECT_URL_SESSION_KEY } from '@/features/shared/constants/analytics'
import { logError } from '@/features/shared/utils/logger'
import { queueOrPushEvent } from '@/features/shared/analytics/google'
import { matchRouteByPath } from '@shared/utils/matchRouteByPath'

type Props = {
  pathname: string
  isInitialNavigation?: boolean
  // SEO measure - capturing previousUrl as referrer value in page event prevents inaccurate SEO attribution for users with long sessions
  previousUrl?: string
}

export const pageEvent = ({ pathname, isInitialNavigation = false, previousUrl }: Props) => {
  if (isOnServer()) return

  const routeData = matchRouteByPath(pathname)
  if (routeData && !MEDALLIA_DELAYED_ROUTES.has(routeData.routeName)) {
    medallia.updatePageView()
  }
  if (shouldDisableAnalytics()) return

  // On initial page load, Read redirect_url from sessionStorage and clear on subsequent navigations
  let redirectUrl: string | undefined
  if (isInitialNavigation) {
    try {
      const stored = window.sessionStorage.getItem(REDIRECT_URL_SESSION_KEY)
      if (stored) {
        redirectUrl = JSON.parse(stored)
      }
    } catch (err) {
      logError(err, {
        customGroupingHash: 'redirectUrlParsing',
        message: 'error parsing Redirect URL Session Value',
      })
      redirectUrl = undefined // Handle JSON parse errors gracefully
    }
  } else {
    // Clear the redirect_url on non-initial navigation to prevent stale data
    window.sessionStorage.removeItem(REDIRECT_URL_SESSION_KEY)
  }

  const anonymousId = getAnonymousId()

  const pageName = routeData?.trackingName || pathname
  const properties: PageEventProperties = {
    ...getPageData(),
    name: urlPathnameToLocationName(),
    redirect_url: redirectUrl,
  }

  if (previousUrl) {
    properties.referrer = previousUrl
  }
  const options = getTrackOptions()

  queueOrPushEvent({
    eventName: 'Loaded a Page',
    properties: {
      ...properties,
      ...(options.userId && { userId: options.userId }),
      segmentAnonymousId: anonymousId ?? '',
    },
    eventId: options.context.eventId ?? '',
  })

  ShiptAnalytics.page(pageName, properties, options)

  // Clean up storage after analytics is sent
  // Ensures redirect_url doesn't persist to subsequent page views
  if (isInitialNavigation && redirectUrl) {
    window.sessionStorage.removeItem(REDIRECT_URL_SESSION_KEY)
  }
}
